import React, { useState, useEffect } from 'react';
import { Clock, Moon, Sun, Copy, Info, ArrowRight, Calculator } from 'lucide-react';
import Header from '../components/Header';
import Footer from '../components/Footer';
import { useScrollToTopOnMount } from '../hooks/useScrollToTop';

interface SleepResult {
  time: string;
  cycles: number;
  totalHours: number;
  isRecommended: boolean;
}

const SleepCalculator = () => {
  const [mode, setMode] = useState<'wake' | 'sleep'>('wake');
  const [selectedTime, setSelectedTime] = useState<string>('');
  const [sleepLatency, setSleepLatency] = useState<number>(15);
  const [results, setResults] = useState<SleepResult[]>([]);
  const [copiedTime, setCopiedTime] = useState<string>('');

  // Scroll to top when component mounts
  useScrollToTopOnMount();

  // Update document title and meta description
  useEffect(() => {
    document.title = 'Sleep Calculator – Find the Best Time to Sleep or Wake Up | Simran Industries';
    
    // Update meta description
    const metaDescription = document.querySelector('meta[name="description"]');
    if (metaDescription) {
      metaDescription.setAttribute('content', 'Use this free online sleep calculator to determine when to sleep or wake up for optimal rest. Based on natural 90-minute sleep cycles.');
    }

    // Add schema markup
    const schema = {
      "@context": "https://schema.org",
      "@type": "WebApplication",
      "name": "Sleep Calculator",
      "description": "Free online sleep calculator to determine optimal sleep and wake times based on 90-minute sleep cycles",
      "url": "https://simranindustries.com/calculators/sleep-calculator",
      "applicationCategory": "HealthApplication",
      "operatingSystem": "Web Browser",
      "offers": {
        "@type": "Offer",
        "price": "0",
        "priceCurrency": "USD"
      },
      "mainEntity": {
        "@type": "FAQPage",
        "mainEntity": [
          {
            "@type": "Question",
            "name": "How do sleep cycles work?",
            "acceptedAnswer": {
              "@type": "Answer",
              "text": "Sleep cycles last approximately 90 minutes each. Each cycle includes light sleep, deep sleep, and REM sleep. Waking up at the end of a complete cycle helps you feel more refreshed and alert."
            }
          },
          {
            "@type": "Question",
            "name": "How much sleep do I need?",
            "acceptedAnswer": {
              "@type": "Answer",
              "text": "Most adults need 7-9 hours of sleep per night, which equals 5-6 complete sleep cycles. The exact amount varies by individual and lifestyle factors."
            }
          },
          {
            "@type": "Question",
            "name": "When should I go to bed?",
            "acceptedAnswer": {
              "@type": "Answer",
              "text": "Use our sleep calculator to find the optimal bedtime based on your desired wake-up time. Consider your sleep latency (time to fall asleep) and aim for complete sleep cycles."
            }
          }
        ]
      }
    };

    // Remove existing schema if any
    const existingSchema = document.querySelector('script[type="application/ld+json"]');
    if (existingSchema) {
      existingSchema.remove();
    }

    // Add new schema
    const script = document.createElement('script');
    script.type = 'application/ld+json';
    script.text = JSON.stringify(schema);
    document.head.appendChild(script);

    // Cleanup on unmount
    return () => {
      const scriptToRemove = document.querySelector('script[type="application/ld+json"]');
      if (scriptToRemove) {
        scriptToRemove.remove();
      }
    };
  }, []);

  // Calculate sleep cycles (90 minutes each)
  const calculateSleepCycles = (targetTime: string, isWakeTime: boolean): SleepResult[] => {
    if (!targetTime) return [];

    const targetDate = new Date();
    const [hours, minutes] = targetTime.split(':').map(Number);
    targetDate.setHours(hours, minutes, 0, 0);

    const results: SleepResult[] = [];
    const cycleMinutes = 90;
    const latencyMinutes = sleepLatency;

    if (isWakeTime) {
      // Calculate sleep times (going backwards from wake time)
      const cycles = [4, 5, 6, 7]; // 6, 7.5, 9, 10.5 hours
      
      cycles.forEach(cycleCount => {
        const totalSleepMinutes = cycleCount * cycleMinutes;
        const sleepTime = new Date(targetDate.getTime() - (totalSleepMinutes + latencyMinutes) * 60000);
        
        results.push({
          time: sleepTime.toLocaleTimeString('en-US', { 
            hour: 'numeric', 
            minute: '2-digit',
            hour12: true 
          }),
          cycles: cycleCount,
          totalHours: totalSleepMinutes / 60,
          isRecommended: cycleCount === 6 // 9 hours is most recommended
        });
      });
    } else {
      // Calculate wake times (going forwards from sleep time)
      const cycles = [4, 5, 6, 7]; // 6, 7.5, 9, 10.5 hours
      
      cycles.forEach(cycleCount => {
        const totalSleepMinutes = cycleCount * cycleMinutes;
        const wakeTime = new Date(targetDate.getTime() + (totalSleepMinutes + latencyMinutes) * 60000);
        
        results.push({
          time: wakeTime.toLocaleTimeString('en-US', { 
            hour: 'numeric', 
            minute: '2-digit',
            hour12: true 
          }),
          cycles: cycleCount,
          totalHours: totalSleepMinutes / 60,
          isRecommended: cycleCount === 6 // 9 hours is most recommended
        });
      });
    }

    return results.sort((a, b) => a.cycles - b.cycles);
  };

  const handleTimeChange = (time: string) => {
    setSelectedTime(time);
    const newResults = calculateSleepCycles(time, mode === 'wake');
    setResults(newResults);
  };

  const handleModeChange = (newMode: 'wake' | 'sleep') => {
    setMode(newMode);
    if (selectedTime) {
      const newResults = calculateSleepCycles(selectedTime, newMode === 'wake');
      setResults(newResults);
    }
  };

  const handlePresetTime = (preset: string) => {
    const now = new Date();
    let targetTime: string;

    switch (preset) {
      case 'now':
        targetTime = now.toTimeString().slice(0, 5);
        break;
      case '15min':
        now.setMinutes(now.getMinutes() + 15);
        targetTime = now.toTimeString().slice(0, 5);
        break;
      case '30min':
        now.setMinutes(now.getMinutes() + 30);
        targetTime = now.toTimeString().slice(0, 5);
        break;
      case '1hour':
        now.setHours(now.getHours() + 1);
        targetTime = now.toTimeString().slice(0, 5);
        break;
      default:
        targetTime = now.toTimeString().slice(0, 5);
    }

    setSelectedTime(targetTime);
    const newResults = calculateSleepCycles(targetTime, mode === 'wake');
    setResults(newResults);
  };

  const copyToClipboard = async (text: string) => {
    try {
      await navigator.clipboard.writeText(text);
      setCopiedTime(text);
      setTimeout(() => setCopiedTime(''), 2000);
    } catch (err) {
      console.error('Failed to copy text: ', err);
    }
  };

  const getCurrentTime = () => {
    const now = new Date();
    return now.toTimeString().slice(0, 5);
  };

  return (
    <div className="min-h-screen bg-gray-50">
      <Header />
      
      {/* Hero Section */}
      <section className="bg-gradient-to-r from-blue-600 to-purple-600 text-white py-20 pt-32">
        <div className="max-w-4xl mx-auto px-4 text-center">
          <div className="flex items-center justify-center mb-4">
            <Calculator className="w-8 h-8 mr-3" />
            <h1 className="text-4xl md:text-5xl font-bold">Sleep Calculator</h1>
          </div>
          <p className="text-xl text-blue-100 max-w-2xl mx-auto leading-relaxed">
            Find the perfect time to sleep or wake up based on natural 90-minute sleep cycles. 
            Wake up feeling refreshed and energized.
          </p>
        </div>
      </section>

      {/* Calculator Section */}
      <section className="py-16">
        <div className="max-w-4xl mx-auto px-4">
          <div className="bg-white rounded-2xl shadow-lg p-8 mb-8">
            {/* Mode Selection */}
            <div className="flex flex-col sm:flex-row gap-4 mb-8">
              <button
                onClick={() => handleModeChange('wake')}
                className={`flex-1 p-4 rounded-xl border-2 transition-all ${
                  mode === 'wake'
                    ? 'border-blue-500 bg-blue-50 text-blue-700'
                    : 'border-gray-200 hover:border-gray-300'
                }`}
              >
                <div className="flex items-center justify-center space-x-2">
                  <Sun className="w-5 h-5" />
                  <span className="font-medium">I want to wake up at:</span>
                </div>
              </button>
              
              <button
                onClick={() => handleModeChange('sleep')}
                className={`flex-1 p-4 rounded-xl border-2 transition-all ${
                  mode === 'sleep'
                    ? 'border-blue-500 bg-blue-50 text-blue-700'
                    : 'border-gray-200 hover:border-gray-300'
                }`}
              >
                <div className="flex items-center justify-center space-x-2">
                  <Moon className="w-5 h-5" />
                  <span className="font-medium">I am going to bed at:</span>
                </div>
              </button>
            </div>

            {/* Time Input */}
            <div className="mb-8">
              <label className="block text-sm font-medium text-gray-700 mb-3">
                {mode === 'wake' ? 'Select your desired wake-up time:' : 'Select your bedtime:'}
              </label>
              
              <div className="flex flex-col sm:flex-row gap-4">
                <input
                  type="time"
                  value={selectedTime}
                  onChange={(e) => handleTimeChange(e.target.value)}
                  className="flex-1 px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent text-lg"
                />
                
                <div className="flex flex-wrap gap-2">
                  <button
                    onClick={() => handlePresetTime('now')}
                    className="px-4 py-2 bg-gray-100 text-gray-700 rounded-lg hover:bg-gray-200 transition-colors text-sm"
                  >
                    Now
                  </button>
                  <button
                    onClick={() => handlePresetTime('15min')}
                    className="px-4 py-2 bg-gray-100 text-gray-700 rounded-lg hover:bg-gray-200 transition-colors text-sm"
                  >
                    +15 min
                  </button>
                  <button
                    onClick={() => handlePresetTime('30min')}
                    className="px-4 py-2 bg-gray-100 text-gray-700 rounded-lg hover:bg-gray-200 transition-colors text-sm"
                  >
                    +30 min
                  </button>
                  <button
                    onClick={() => handlePresetTime('1hour')}
                    className="px-4 py-2 bg-gray-100 text-gray-700 rounded-lg hover:bg-gray-200 transition-colors text-sm"
                  >
                    +1 hour
                  </button>
                </div>
              </div>
            </div>

            {/* Sleep Latency Setting */}
            <div className="mb-8">
              <label className="block text-sm font-medium text-gray-700 mb-3">
                Time to fall asleep (sleep latency):
              </label>
              <div className="flex items-center space-x-4">
                <input
                  type="range"
                  min="5"
                  max="30"
                  step="5"
                  value={sleepLatency}
                  onChange={(e) => {
                    setSleepLatency(Number(e.target.value));
                    if (selectedTime) {
                      const newResults = calculateSleepCycles(selectedTime, mode === 'wake');
                      setResults(newResults);
                    }
                  }}
                  className="flex-1"
                />
                <span className="text-sm font-medium text-gray-600 min-w-[60px]">
                  {sleepLatency} min
                </span>
              </div>
            </div>

            {/* Results */}
            {results.length > 0 && (
              <div className="space-y-4">
                <h3 className="text-lg font-semibold text-gray-900 mb-4">
                  {mode === 'wake' ? 'Recommended sleep times:' : 'Recommended wake-up times:'}
                </h3>
                
                <div className="grid gap-4">
                  {results.map((result, index) => (
                    <div
                      key={index}
                      className={`p-4 rounded-xl border-2 transition-all ${
                        result.isRecommended
                          ? 'border-green-500 bg-green-50'
                          : 'border-gray-200 hover:border-gray-300'
                      }`}
                    >
                      <div className="flex items-center justify-between">
                        <div className="flex items-center space-x-4">
                          <div className="text-center">
                            <div className={`text-2xl font-bold ${
                              result.isRecommended ? 'text-green-700' : 'text-gray-900'
                            }`}>
                              {result.time}
                            </div>
                            <div className="text-sm text-gray-600">
                              {result.cycles} cycles • {result.totalHours} hours
                            </div>
                          </div>
                          
                          {result.isRecommended && (
                            <div className="flex items-center space-x-1 text-green-700">
                              <Info className="w-4 h-4" />
                              <span className="text-sm font-medium">Recommended</span>
                            </div>
                          )}
                        </div>
                        
                        <button
                          onClick={() => copyToClipboard(result.time)}
                          className="p-2 text-gray-500 hover:text-gray-700 transition-colors"
                          title="Copy to clipboard"
                        >
                          {copiedTime === result.time ? (
                            <span className="text-green-600">✓</span>
                          ) : (
                            <Copy className="w-4 h-4" />
                          )}
                        </button>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            )}
          </div>

          {/* SEO Content */}
          <div className="bg-white rounded-2xl shadow-lg p-8">
            <h2 className="text-3xl font-bold text-gray-900 mb-6">
              How Sleep Cycles Work
            </h2>
            
            <div className="prose max-w-none text-gray-700 space-y-6">
              <p className="text-lg leading-relaxed">
                Sleep is not just a continuous state of rest. Your body goes through multiple sleep cycles, 
                each lasting approximately 90 minutes. Each cycle consists of different stages of sleep, 
                from light sleep to deep sleep and REM (Rapid Eye Movement) sleep.
              </p>
              
              <div className="grid md:grid-cols-2 gap-6 my-8">
                <div className="bg-blue-50 p-6 rounded-xl">
                  <h3 className="text-xl font-semibold text-blue-900 mb-3">Why 90-Minute Cycles?</h3>
                  <p className="text-blue-800">
                    The 90-minute sleep cycle is based on your body's natural circadian rhythm. 
                    Waking up at the end of a complete cycle helps you feel more refreshed and alert, 
                    while waking up in the middle of a cycle can leave you feeling groggy and tired.
                  </p>
                </div>
                
                <div className="bg-green-50 p-6 rounded-xl">
                  <h3 className="text-xl font-semibold text-green-900 mb-3">Benefits of Proper Sleep</h3>
                  <ul className="text-green-800 space-y-2">
                    <li>• Improved focus and concentration</li>
                    <li>• Better mood and emotional regulation</li>
                    <li>• Enhanced physical performance</li>
                    <li>• Stronger immune system</li>
                    <li>• Better memory consolidation</li>
                  </ul>
                </div>
              </div>
              
              <h3 className="text-2xl font-bold text-gray-900 mt-8 mb-4">
                How to Use This Sleep Calculator
              </h3>
              
              <div className="grid md:grid-cols-2 gap-6">
                <div>
                  <h4 className="text-lg font-semibold text-gray-900 mb-2">Option 1: I want to wake up at...</h4>
                  <p className="text-gray-700">
                    Select your desired wake-up time, and the calculator will show you the best times to go to bed. 
                    Choose a time that gives you 6-9 hours of sleep for optimal rest.
                  </p>
                </div>
                
                <div>
                  <h4 className="text-lg font-semibold text-gray-900 mb-2">Option 2: I am going to bed at...</h4>
                  <p className="text-gray-700">
                    Select your bedtime, and the calculator will show you the best times to wake up. 
                    This ensures you complete full sleep cycles and wake up feeling refreshed.
                  </p>
                </div>
              </div>
              
              <div className="bg-amber-50 border border-amber-200 rounded-xl p-6 mt-8">
                <h4 className="text-lg font-semibold text-amber-900 mb-3">
                  💡 Pro Tips for Better Sleep
                </h4>
                <ul className="text-amber-800 space-y-2">
                  <li>• Maintain a consistent sleep schedule, even on weekends</li>
                  <li>• Create a relaxing bedtime routine</li>
                  <li>• Keep your bedroom cool, dark, and quiet</li>
                  <li>• Avoid screens 1-2 hours before bedtime</li>
                  <li>• Exercise regularly, but not too close to bedtime</li>
                  <li>• Avoid caffeine and large meals before sleep</li>
                </ul>
              </div>
            </div>
          </div>
        </div>
      </section>

      <Footer />
    </div>
  );
};

export default SleepCalculator; 